import React, { useState, useEffect, useMemo } from "react";
import {
  Alert,
  AlertTitle,
  AppBar,
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Step,
  StepLabel,
  StepContent,
  Stepper,
  TextField,
  Toolbar,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@material-ui/icons/Close";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import StorefrontIcon from "@material-ui/icons/Storefront";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { toast } from "react-toastify";

import ImageGallery from "react-image-gallery";
import LoadingModal from "./LoadingModal";
import ItemCardOpcao from "./ItemCardOpcao";
import numeral from "numeral";
import Config from "../config/config";

import v2_actionBuscarProduto from "../actions/v2/produtos/v2_actionBuscarProduto.js";
import { useDispatch, useSelector } from "react-redux";
import actionIsConfereEstoqueLoja from "../actions/config/actionIsConfereEstoqueLoja.js";
import v2_actionCoresProdutos from "../actions/v2/produtos/v2_actionCoresProdutos.js";
import v2_actionTamanhosProdutos from "../actions/v2/produtos/v2_actionTamanhosProdutos.js";
import v2_actionProdutosLojas from "../actions/v2/produtos/v2_actionProdutosLojas.js";
import v2_actionPrecosProdutos from "../actions/v2/produtos/v2_actionPrecosProdutos.js";

import { getDataUsuario } from "../services/auth.js";
import { formatarCodigoFilial } from "../utils/functions.js";
import { addToCart } from "../actions/index.js";
import Actions from "../constants/Actions.js";

const DEFAULT_IMAGE = "https://katuxa.sfo3.digitaloceanspaces.com/pdv/sem-imagem.jpg";

const agruparPorGrade = (produtos) =>
  produtos.reduce((acc, item) => {
    const { estoque, produto } = item;
    const { grade } = produto;
    if (!acc[grade]) acc[grade] = { grade, estoque };
    else acc[grade].estoque += estoque;
    return acc;
  }, {});

const transformarEmArray = (objetoAgrupado) => Object.values(objetoAgrupado);

// Regra de negócio: produtos do grupo BOTA ou da linha FEMININO sempre exigem
// confirmação visual do vendedor antes de ir ao carrinho, mesmo com estoque > 1.
const requerConfirmacaoVisual = (produto, estoque) => {
  if (estoque === 1) return true;
  if (produto?.grupo_produto === "BOTA") return true;
  if (produto?.linha === "FEMININO") return true;
  return false;
};

const getProductImages = (verProduto, maxImages = 5) => {
  if (!verProduto || !verProduto.images || verProduto.images.length === 0) {
    return [{ original: DEFAULT_IMAGE, thumbnail: DEFAULT_IMAGE }];
  }
  return Array.from({ length: maxImages }, (_, index) => {
    const imageUrl =
      verProduto.images.length > index
        ? `${Config.base_fotos}/fotos/${verProduto.produto}_${verProduto.cor_produto}_0${index + 1}.jpg`
        : DEFAULT_IMAGE;
    return { original: imageUrl, thumbnail: imageUrl };
  });
};

const ModalProduto = ({ open, handleClickClose, produto, onLoadProduct }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const [outrasCoresProduto, setOutrasCoresProduto] = useState([]);
  const [outrosTamanhos, setOutrosTamanhos] = useState([]);
  const [filiaisProdutos, setFiliaisProdutos] = useState([]);
  const [tamanhoProduto, setTamanhoProduto] = useState("");
  const [corProduto, setCorProduto] = useState("");
  const [filialProduto, setFilialProduto] = useState("");

  const [modalTravaCarrinho, setModalTravaCarrinho] = useState(false);
  const [loadingInicial, setLoadingInicial] = useState(false);
  const [loadingTamanhos, setLoadingTamanhos] = useState(false);
  const [loadingFiliais, setLoadingFiliais] = useState(false);
  const [loadingVerificaPreco, setLoadingVerificaPreco] = useState(false);

  const [isProdutoApto, setIsProdutoApto] = useState(false);
  const [produtoAbaixoEstoque, setProdutoAbaixoEstoque] = useState(false);
  const [precoProduto, setPrecoProduto] = useState("");
  const [isConfereEstoqueLoja, setIsConfereEstoqueLoja] = useState(false);
  const [isEstoque, setIsEstoque] = useState(false);

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideTamanho, setOverrideTamanho] = useState("");
  const [overrideLoja, setOverrideLoja] = useState("");
  const [overrideCiente, setOverrideCiente] = useState(false);

  const cepOrigemProduto = useSelector((state) => state.cepOrigemProduto);
  const cartItems = useSelector((state) => state.cartItems);

  const verProduto = produto;
  const imagensProduto = useMemo(() => getProductImages(verProduto), [verProduto]);

  const corSelecionadaLabel = useMemo(() => {
    if (!corProduto) return "";
    const match = outrasCoresProduto.find((c) => c.use_cod_cor_produto === corProduto);
    return match?.use_cor_produto || verProduto?.use_cor_produto || corProduto;
  }, [corProduto, outrasCoresProduto, verProduto]);

  const activeStep = useMemo(() => {
    if (filialProduto) return 3;
    if (tamanhoProduto) return 2;
    if (corProduto) return 1;
    return 0;
  }, [corProduto, tamanhoProduto, filialProduto]);

  const resetSelections = () => {
    setCorProduto("");
    setTamanhoProduto("");
    setFilialProduto("");
    setFiliaisProdutos([]);
    setOutrosTamanhos([]);
    setOutrasCoresProduto([]);
    setPrecoProduto("");
    setIsProdutoApto(false);
    setProdutoAbaixoEstoque(false);
  };

  const onCloseModal = () => {
    resetSelections();
    handleClickClose();
  };

  const handleAdicionarCarrinho = (product) => {
    const item = {
      product_image: product.images.length > 0 ? product.images[0]._id : null,
      product_id: product._id,
      variant_id: null,
      quantity: 1,
      discount_total: 0,
      name: product.name,
      regular_price: precoProduto,
      price: precoProduto,
      price_total: precoProduto,
      sku: product.sku,
      tax_class: 0,
      tax_total: 0,
      produto: product.produto,
      cor_produto: corProduto,
      grade: tamanhoProduto,
      variant_name: null,
      weight: product.weight,
      dimension: product.dimension,
      images: product.images,
      filial: formatarCodigoFilial(filialProduto),
      desc_cor_produto: product.desc_cor_produto,
      length: product.dimensions.length,
      heigth: product.dimensions.height,
      width: product.dimensions.width,
      use_descricao: product.use_descricao,
      use_referencia: product.use_referencia,
      use_cod_cor_produto: product.use_cod_cor_produto,
      use_cod_filial: product.use_cod_filial,
      use_cor_produto: product.use_cor_produto,
    };

    const adicionar = () => {
      dispatch(addToCart(item));
      dispatch({ type: Actions.CEP_ORIGEM_PRODUTO, cepOrigemProduto: filialProduto });
      onCloseModal();
      dispatch({ type: Actions.DRAWER_CARRINHO, drawerCarrinho: true });
    };

    if (cartItems.length === 0 || filialProduto === cepOrigemProduto) {
      adicionar();
    } else {
      setModalTravaCarrinho(true);
    }
  };

  const handleEscolheuCor = async (referencia, cor) => {
    setCorProduto(cor);
    setOutrosTamanhos([]);
    setTamanhoProduto("");
    setFilialProduto("");
    setFiliaisProdutos([]);
    setPrecoProduto("");
    setIsProdutoApto(false);
    setProdutoAbaixoEstoque(false);
    setLoadingTamanhos(true);

    const querys = `?referencia=${referencia}&cor_produto=${cor}`;
    const res = await v2_actionBuscarProduto(querys);
    if (res.status === 200 && res.data.produto) {
      onLoadProduct(res.data.produto);
    }

    const tamanhos = await v2_actionTamanhosProdutos(
      `?referencia=${referencia}&cor_produto=${cor}&loja=${getDataUsuario().filial}`
    );
    if (tamanhos.status === 200 && tamanhos.data.produtos.length > 0) {
      const agrupados = agruparPorGrade(tamanhos.data.produtos);
      setOutrosTamanhos(transformarEmArray(agrupados));
      setIsEstoque(true);
    } else {
      setIsEstoque(false);
    }
    setLoadingTamanhos(false);
  };

  const handleEscolheuTamanho = async (data) => {
    setProdutoAbaixoEstoque(false);
    setIsProdutoApto(false);
    setFilialProduto("");
    setFiliaisProdutos([]);
    setPrecoProduto("");
    setTamanhoProduto(data.grade);

    if (requerConfirmacaoVisual(verProduto, data.estoque)) {
      setProdutoAbaixoEstoque(true);
    } else {
      setIsProdutoApto(true);
    }

    setLoadingFiliais(true);
    const produtosLojas = await v2_actionProdutosLojas(
      `?referencia=${verProduto.use_referencia}&cor_produto=${verProduto.use_cod_cor_produto}&tamanho=${data.grade}&loja_vendedor=${getDataUsuario().filial}`
    );
    if (produtosLojas.status === 200 && produtosLojas.data.produtos.length > 0) {
      setFiliaisProdutos(produtosLojas.data.produtos);
    }
    setLoadingFiliais(false);
  };

  const resetParaEscolhaTamanho = () => {
    setTamanhoProduto("");
    setFilialProduto("");
    setFiliaisProdutos([]);
    setPrecoProduto("");
    setIsProdutoApto(false);
    setProdutoAbaixoEstoque(false);
  };

  const handleEscolheuFilial = async (data, { tamanhoOverride } = {}) => {
    setLoadingVerificaPreco(true);
    setProdutoAbaixoEstoque(false);
    setIsProdutoApto(false);

    if (requerConfirmacaoVisual(verProduto, data.estoque)) {
      setProdutoAbaixoEstoque(true);
    } else {
      setIsProdutoApto(true);
    }

    const filialEscolhida = data.loja;
    const tamanhoUsado = tamanhoOverride || tamanhoProduto;

    try {
      const precos = await v2_actionPrecosProdutos(
        `?referencia=${verProduto.use_referencia}&cor_produto=${verProduto.use_cod_cor_produto}&tamanho=${tamanhoUsado}&loja=${filialEscolhida}`
      );

      if (precos.status === 200 && precos.data.preco) {
        setPrecoProduto(precos.data.preco);
        setFilialProduto(filialEscolhida);
      } else {
        resetParaEscolhaTamanho();
        toast.error(
          `Não foi possível obter o preço para a loja ${filialEscolhida} no tamanho ${tamanhoUsado}. Selecione o tamanho novamente.`,
          { position: "top-right", autoClose: 5000 }
        );
      }
    } catch (err) {
      console.error("Erro ao buscar preço do produto:", err);
      resetParaEscolhaTamanho();
      toast.error(
        "Erro ao consultar o preço do produto. Verifique se o produto é correto e se existe e tente novamente. Se o problema persistir, entre em contato com o suporte.",
        { position: "top-right", autoClose: 15000 }
      );
    } finally {
      setLoadingVerificaPreco(false);
    }
  };

  const handleAbrirOverride = () => {
    setOverrideOpen(true);
  };

  const handleFecharOverride = () => {
    setOverrideOpen(false);
    setOverrideTamanho("");
    setOverrideLoja("");
    setOverrideCiente(false);
  };

  const handleConfirmarOverride = async () => {
    const novoTamanho = overrideTamanho.trim().toUpperCase();
    const novaLoja = overrideLoja.trim();
    if (!novoTamanho || !novaLoja || !overrideCiente) return;

    setTamanhoProduto(novoTamanho);
    setIsEstoque(true);
    setFiliaisProdutos([{ loja: novaLoja, estoque: 1 }]);
    setOverrideOpen(false);
    setOverrideTamanho("");
    setOverrideLoja("");
    setOverrideCiente(false);

    await handleEscolheuFilial({ loja: novaLoja, estoque: 1 }, { tamanhoOverride: novoTamanho });
  };

  const overrideConfirmDisabled =
    !overrideTamanho.trim() || !overrideLoja.trim() || !overrideCiente;

  const handleChangeIsProdutoApto = (event) => {
    setIsProdutoApto(event.target.checked);
  };

  useEffect(() => {
    actionIsConfereEstoqueLoja()
      .then((res) => setIsConfereEstoqueLoja(res.data.data))
      .catch((err) => console.error("Erro ao verificar estoque loja:", err));
  }, []);

  useEffect(() => {
    if (!verProduto?.use_cod_cor_produto) return;

    setLoadingInicial(true);
    setCorProduto(verProduto.use_cod_cor_produto);

    const carregarCores = async () => {
      try {
        const cores = await v2_actionCoresProdutos(`?referencia=${verProduto.use_referencia}`);
        if (cores.status === 200) {
          setOutrasCoresProduto(cores.data.cores);
          await handleEscolheuCor(verProduto.use_referencia, verProduto.use_cod_cor_produto);
        }
      } catch (error) {
        console.error("Erro ao carregar cores:", error);
      } finally {
        setLoadingInicial(false);
      }
    };

    carregarCores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verProduto?.use_referencia]);

  const renderTamanhoButton = (data) => {
    const isSelected = tamanhoProduto === data.grade;
    const isDisabled = data.estoque === 0;
    const badgeColor = data.estoque === 1 ? "warning" : data.estoque === 0 ? "error" : "primary";

    return (
      <Badge
        key={data.grade}
        badgeContent={data.estoque}
        color={badgeColor}
        sx={{
          mr: 0.5,
          mb: 0.5,
          "& .MuiBadge-badge": { fontSize: 10, height: 16, minWidth: 16, right: 4, top: 2 },
        }}
        showZero
      >
        <ToggleButton
          value={data.grade}
          selected={isSelected}
          disabled={isDisabled}
          onClick={() => !isDisabled && handleEscolheuTamanho(data)}
          sx={{
            minWidth: 48,
            px: 1.25,
            py: 0.5,
            fontWeight: 700,
            fontSize: 13,
            lineHeight: 1.2,
            borderRadius: 1,
            borderColor: isSelected ? "primary.main" : "divider",
            "&.Mui-selected": {
              bgcolor: "primary.main",
              color: "primary.contrastText",
              borderColor: "primary.main",
              "&:hover": { bgcolor: "primary.dark" },
            },
          }}
        >
          {data.grade === "UN" ? "UN" : data.grade}
        </ToggleButton>
      </Badge>
    );
  };

  const renderFilialCard = (data, index) => {
    const isSelected = filialProduto === data.loja;
    const isCritico = data.estoque === 1;
    return (
      <Badge
        key={`${data.loja}-${index}`}
        badgeContent={data.estoque}
        color={isCritico ? "error" : "primary"}
        sx={{
          width: "100%",
          "& .MuiBadge-badge": { right: 4, top: 4, fontSize: 10, height: 14, minWidth: 14, padding: "0 4px" },
        }}
      >
        <Box
          onClick={() => handleEscolheuFilial(data)}
          sx={{
            width: "100%",
            cursor: "pointer",
            px: 1,
            py: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            borderRadius: 1,
            border: 1,
            borderStyle: "solid",
            borderColor: isSelected ? "success.main" : "divider",
            bgcolor: isSelected ? "success.lighter" : "background.paper",
            transition: "all 0.12s ease",
            "&:hover": { borderColor: "success.light" },
          }}
        >
          <StorefrontIcon sx={{ fontSize: 14 }} color={isSelected ? "success" : "action"} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1 }}>
            LOJA {data.loja}
          </Typography>
        </Box>
      </Badge>
    );
  };

  const stepCorLabel = corProduto ? `Cor: ${corSelecionadaLabel}` : "Escolha a cor";
  const stepTamanhoLabel = tamanhoProduto ? `Tamanho: ${tamanhoProduto}` : "Escolha o tamanho";
  const stepFilialLabel = filialProduto ? `Filial: LOJA ${filialProduto}` : "Escolha a filial";

  return (
    <Dialog fullScreen open={open} className="dialog-ver-produto" onClose={onCloseModal}>
      <AppBar position="sticky" color="primary" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onCloseModal} aria-label="fechar">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 1, flex: 1 }}>
            Detalhes do produto
          </Typography>
        </Toolbar>
      </AppBar>

      {loadingInicial ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
          <LoadingModal />
        </Stack>
      ) : (
        <Container maxWidth="xl" sx={{ mt: 3, mb: 4, px: { xs: 2, md: 4 } }}>
          <Grid container spacing={4}>
            <Grid item xl={5} lg={5} md={6} sm={12} xs={12}>
              <Box sx={{ position: { lg: "sticky" }, top: { lg: 88 } }}>
                <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", bgcolor: "grey.50", p: 1 }}>
                  <ImageGallery showPlayButton={false} items={imagensProduto} />
                </Paper>
              </Box>
            </Grid>

            <Grid item xl={7} lg={7} md={6} sm={12} xs={12}>
              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {verProduto?.use_descricao}
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Typography variant="caption" color="text.secondary">
                    REF: <strong>{verProduto?.use_referencia}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    COR: <strong>{verProduto?.use_cor_produto}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    COD: <strong>{verProduto?.use_cod_cor_produto}</strong>
                  </Typography>
                </Stack>
              </Stack>

              <Divider sx={{ mb: 1.5 }} />

              <Stepper
                activeStep={activeStep}
                orientation="vertical"
                nonLinear
                sx={{
                  "& .MuiStep-root": { pb: 0 },
                  "& .MuiStepContent-root": { ml: 1.5, pl: 2.5, py: 0.5, borderLeft: "1px dashed", borderColor: "divider" },
                  "& .MuiStepConnector-root": { ml: 1.5 },
                  "& .MuiStepConnector-line": { borderLeftStyle: "dashed", minHeight: 6 },
                  "& .MuiStepLabel-root": { py: 0.5 },
                  "& .MuiStepLabel-label": { fontSize: 15, fontWeight: 600 },
                  "& .MuiStepLabel-label.Mui-active": { fontWeight: 700 },
                }}
              >
                {/* Step 1 — Cor */}
                <Step active={activeStep >= 0} completed={activeStep > 0}>
                  <StepLabel
                    optional={
                      activeStep > 0 ? (
                        <Typography variant="caption" color="success.main">
                          Concluído
                        </Typography>
                      ) : null
                    }
                  >
                    {stepCorLabel}
                  </StepLabel>
                  <StepContent TransitionProps={{ unmountOnExit: false }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
                      {outrasCoresProduto.length === 0
                        ? Array.from({ length: 6 }).map((_, i) => (
                            <Skeleton key={i} variant="rounded" width={110} height={28} />
                          ))
                        : outrasCoresProduto.map((cor, index) => {
                            const isSel = corProduto === cor.use_cod_cor_produto;
                            return (
                              <Box
                                key={index}
                                onClick={() =>
                                  handleEscolheuCor(verProduto.use_referencia, cor.use_cod_cor_produto)
                                }
                                sx={{
                                  cursor: "pointer",
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: isSel ? "primary.main" : "divider",
                                  bgcolor: isSel ? "primary.lighter" : "background.paper",
                                  fontSize: 12,
                                  fontWeight: isSel ? 700 : 500,
                                  color: isSel ? "primary.dark" : "text.primary",
                                  transition: "all 0.15s ease",
                                  "&:hover": { borderColor: "primary.light" },
                                }}
                              >
                                {cor.use_cor_produto}
                              </Box>
                            );
                          })}
                    </Box>
                  </StepContent>
                </Step>

                {/* Step 2 — Tamanho */}
                <Step active={activeStep >= 1} completed={activeStep > 1} disabled={!corProduto}>
                  <StepLabel
                    optional={
                      activeStep > 1 ? (
                        <Typography variant="caption" color="success.main">
                          Concluído
                        </Typography>
                      ) : null
                    }
                  >
                    {stepTamanhoLabel}
                  </StepLabel>
                  <StepContent TransitionProps={{ unmountOnExit: false }}>
                    {loadingTamanhos ? (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Skeleton key={i} variant="rounded" width={56} height={40} />
                        ))}
                      </Stack>
                    ) : isEstoque ? (
                      <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap" }}>
                        <ToggleButtonGroup
                          value={tamanhoProduto}
                          exclusive
                          sx={{ flexWrap: "wrap", gap: 0.5, "& .MuiToggleButtonGroup-grouped": { border: 1 } }}
                        >
                          {outrosTamanhos.map(renderTamanhoButton)}
                        </ToggleButtonGroup>
                      </Box>
                    ) : (
                      <Alert
                        severity="warning"
                        icon={<ErrorOutlineIcon />}
                        sx={{ mt: 1, borderRadius: 2 }}
                      >
                        <AlertTitle>Produto sem estoque</AlertTitle>
                        Tente outra cor ou entre em contato com o suporte.
                      </Alert>
                    )}
                    {!loadingTamanhos && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<HelpOutlineIcon />}
                          onClick={handleAbrirOverride}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          Não retornou o tamanho
                        </Button>
                      </Box>
                    )}
                  </StepContent>
                </Step>

                {/* Step 3 — Filial */}
                <Step active={activeStep >= 2} completed={activeStep > 2} disabled={!tamanhoProduto}>
                  <StepLabel
                    optional={
                      activeStep > 2 ? (
                        <Typography variant="caption" color="success.main">
                          Concluído
                        </Typography>
                      ) : null
                    }
                  >
                    {stepFilialLabel}
                  </StepLabel>
                  <StepContent TransitionProps={{ unmountOnExit: false }}>
                    <Box
                      sx={{
                        mt: 0.25,
                        display: "grid",
                        gap: 0.75,
                        gridTemplateColumns: {
                          xs: "repeat(3, 1fr)",
                          sm: "repeat(4, 1fr)",
                          md: "repeat(5, 1fr)",
                          lg: "repeat(6, 1fr)",
                          xl: "repeat(8, 1fr)",
                        },
                      }}
                    >
                      {loadingFiliais
                        ? Array.from({ length: 10 }).map((_, i) => (
                            <Skeleton key={i} variant="rounded" height={28} />
                          ))
                        : filiaisProdutos.map((data, i) => renderFilialCard(data, i))}
                    </Box>
                    {loadingVerificaPreco && <LinearProgress sx={{ mt: 2 }} />}
                  </StepContent>
                </Step>

                {/* Step 4 — Confirmação */}
                <Step active={activeStep >= 3} completed={false} disabled={!filialProduto}>
                  <StepLabel
                    icon={
                      activeStep >= 3 ? (
                        <CheckCircleIcon color="primary" />
                      ) : undefined
                    }
                  >
                    Confirmação
                  </StepLabel>
                  <StepContent TransitionProps={{ unmountOnExit: false }}>
                    {filialProduto && (
                      <Paper
                        elevation={0}
                        sx={{
                          mt: 1,
                          p: 2.5,
                          borderRadius: 2,
                          bgcolor: "primary.lighter",
                          border: 1,
                          borderColor: "primary.light",
                        }}
                      >
                        {isConfereEstoqueLoja ? (
                          <>
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              alignItems={{ xs: "flex-start", sm: "center" }}
                              justifyContent="space-between"
                              spacing={2}
                              sx={{ mb: produtoAbaixoEstoque ? 2 : 2.5 }}
                            >
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                                  Preço a prazo
                                </Typography>
                                <Stack direction="row" alignItems="baseline" spacing={0.75}>
                                  <Typography variant="body1" color="primary.dark" fontWeight={700}>
                                    R$
                                  </Typography>
                                  <Typography variant="h3" color="primary.dark" fontWeight={800} sx={{ lineHeight: 1 }}>
                                    {numeral(precoProduto).format("0,0.00")}
                                  </Typography>
                                </Stack>
                              </Box>
                              <Stack spacing={0.25} sx={{ minWidth: 140 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Resumo
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {corSelecionadaLabel} · Tam. {tamanhoProduto}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  LOJA {filialProduto}
                                </Typography>
                              </Stack>
                            </Stack>
                            {produtoAbaixoEstoque && (
                              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isProdutoApto}
                                      onChange={handleChangeIsProdutoApto}
                                    />
                                  }
                                  label={`Confirmo que verifiquei o produto na filial ${filialProduto} e está apto para venda`}
                                />
                              </Alert>
                            )}
                            <Button
                              fullWidth
                              size="large"
                              variant="contained"
                              color="primary"
                              disabled={!isProdutoApto}
                              onClick={() => handleAdicionarCarrinho(verProduto)}
                              sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, fontSize: 16 }}
                            >
                              Adicionar ao carrinho
                            </Button>
                          </>
                        ) : (
                          <Button
                            fullWidth
                            size="large"
                            variant="contained"
                            color="primary"
                            onClick={() => handleAdicionarCarrinho(verProduto)}
                            sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, fontSize: 16 }}
                          >
                            Adicionar ao carrinho
                          </Button>
                        )}
                      </Paper>
                    )}
                  </StepContent>
                </Step>
              </Stepper>
            </Grid>
          </Grid>
        </Container>
      )}

      <Dialog
        open={modalTravaCarrinho}
        onClose={() => setModalTravaCarrinho(false)}
        aria-labelledby="alert-trava-carrinho-title"
      >
        <DialogTitle id="alert-trava-carrinho-title">
          Não é possível misturar filiais no carrinho
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Não fechamos venda com produtos de filiais diferentes. Finalize ou esvazie o carrinho atual antes de adicionar este item.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalTravaCarrinho(false)} color="primary" autoFocus>
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={overrideOpen}
        onClose={handleFecharOverride}
        aria-labelledby="alert-override-tamanho-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="alert-override-tamanho-title">
          Tamanho/Loja não retornados pela busca
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <AlertTitle>Atenção</AlertTitle>
            Use esta função apenas se você verificou fisicamente que a loja informada
            possui o tamanho que vai inserir. Você é responsável pela conferência.
          </Alert>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Tamanho"
              value={overrideTamanho}
              onChange={(e) => setOverrideTamanho(e.target.value)}
              fullWidth
              size="small"
              autoFocus
            />
            <TextField
              label="Loja"
              value={overrideLoja}
              onChange={(e) => setOverrideLoja(e.target.value)}
              fullWidth
              size="small"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={overrideCiente}
                  onChange={(e) => setOverrideCiente(e.target.checked)}
                />
              }
              label="Confirmo que verifiquei fisicamente o produto na loja informada"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharOverride} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarOverride}
            color="primary"
            variant="contained"
            disabled={overrideConfirmDisabled}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ModalProduto;
